import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);

// Iter48 — hierarchical package tiers. Higher tier includes everything in lower ones.
const PACKAGE_RANK = { pdf: 0, cad: 1, revit: 2 };
const PACKAGE_LABEL = { pdf: "PDF", cad: "CAD", revit: "Revit" };
export const PACKAGE_TIERS = ["pdf", "cad", "revit"];
export const PACKAGE_LABELS = PACKAGE_LABEL;

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dh_cart") || "[]"); } catch (e) { if (typeof console !== "undefined") console.debug("[silent]", e); return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dh_wishlist") || "[]"); } catch (e) { if (typeof console !== "undefined") console.debug("[silent]", e); return []; }
  });
  const [compareList, setCompareList] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dh_compare") || "[]"); } catch (e) { if (typeof console !== "undefined") console.debug("[silent]", e); return []; }
  });
  const [compareOpen, setCompareOpen] = useState(false);
  const [currency, setCurrency] = useState(() => localStorage.getItem("dh_currency") || "usd");

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 = anonymous visitor, expected. Silent for that case; log
      // anything else so real auth breakages still surface.
      if (err?.response?.status !== 401) {
        // [removed console.debug]
      }
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { localStorage.setItem("dh_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("dh_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("dh_compare", JSON.stringify(compareList)); }, [compareList]);
  useEffect(() => { localStorage.setItem("dh_currency", currency); }, [currency]);

  const login = async (email, password, turnstile_token) => {
    const { data } = await api.post("/auth/login", { email, password, turnstile_token });
    localStorage.setItem("dh_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, turnstile_token) => {
    const { data } = await api.post("/auth/register", { name, email, password, turnstile_token });
    localStorage.setItem("dh_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) { if (typeof console !== "undefined") console.debug("[silent]", e); return null; }
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout API call failed:", err);
    }
    localStorage.removeItem("dh_token");
    setUser(null);
  };

  const addToCart = (plan, packageTier = "pdf") => {
    const tier = PACKAGE_RANK.hasOwnProperty(packageTier) ? packageTier : "pdf";
    setCart((prev) => {
      const existing = prev.find((p) => p.id === plan.id);
      if (!existing) {
        // First time in cart at this tier.
        return [...prev, { ...plan, package_tier: tier }];
      }
      const existingRank = PACKAGE_RANK[existing.package_tier || "pdf"];
      const newRank = PACKAGE_RANK[tier];
      if (newRank === existingRank) {
        // Same tier — no-op (Cart already has it).
        return prev;
      }
      if (newRank > existingRank) {
        // Upgrade — replace with higher tier, refresh plan snapshot.
        toast.success(
          `Package upgraded to ${PACKAGE_LABEL[tier]}`,
          { description: `Your ${PACKAGE_LABEL[existing.package_tier || "pdf"]} package for ${plan.title} was replaced.` }
        );
        return prev.map((p) => p.id === plan.id ? { ...plan, package_tier: tier } : p);
      }
      // Attempted downgrade — block. Higher tier already includes it.
      toast.info(
        `Already in cart with ${PACKAGE_LABEL[existing.package_tier || "pdf"]} package`,
        { description: `That package already includes ${PACKAGE_LABEL[tier]} deliverables.` }
      );
      return prev;
    });
  };
  const removeFromCart = (planId) => setCart((prev) => prev.filter((p) => p.id !== planId));
  const clearCart = () => setCart([]);

  const toggleWishlist = (plan) => {
    setWishlist((prev) => prev.find((p) => p.id === plan.id)
      ? prev.filter((p) => p.id !== plan.id)
      : [...prev, plan]);
  };
  const inWishlist = (planId) => wishlist.some((p) => p.id === planId);

  const COMPARE_MAX = 4;
  const toggleCompare = (plan) => {
    setCompareList((prev) => {
      if (prev.find((p) => p.id === plan.id)) return prev.filter((p) => p.id !== plan.id);
      if (prev.length >= COMPARE_MAX) return prev; // capped — UI should signal max reached
      return [...prev, plan];
    });
  };
  const removeFromCompare = (planId) => setCompareList((prev) => prev.filter((p) => p.id !== planId));
  const clearCompare = () => setCompareList([]);
  const inCompare = (planId) => compareList.some((p) => p.id === planId);
  const openCompare = () => setCompareOpen(true);
  const closeCompare = () => setCompareOpen(false);

  const value = {
    user, setUser, authLoading, checkAuth, refreshUser,
    login, register, logout,
    cart, addToCart, removeFromCart, clearCart,
    wishlist, toggleWishlist, inWishlist,
    compareList, toggleCompare, removeFromCompare, clearCompare, inCompare,
    compareOpen, openCompare, closeCompare, COMPARE_MAX,
    currency, setCurrency,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
